import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import type { BatonShape } from '@relaystation-v3/web-elements/baton-pricing-calculator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BatonPricingCalculator } from '@/components/BatonPricingCalculator';
import { ApiError } from '@/lib/fetcher';
import {
  type CreateBatonInput,
  createBaton,
  putPresigned,
} from '@/lib/queries';

/**
 * Mirrors the server tunable `baton.upload.max_single_call_bytes`
 * (default 3 MB). Content at or below this rides inline on the create
 * call; larger files take the presigned-PUT path. Not exposed by an API
 * read, so it tracks the default here — bump if the operator raises the
 * tunable.
 */
const MAX_INLINE_BYTES = 3_000_000;

type ContentMode = 'text' | 'file';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export function BatonCreate() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [shape, setShape] = React.useState<BatonShape | null>(null);
  const [mode, setMode] = React.useState<ContentMode>('text');
  const [text, setText] = React.useState('');
  const [textType, setTextType] = React.useState('text/plain');
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  const provision = useMutation({
    mutationFn: async (): Promise<string> => {
      if (!shape) throw new ApiError(400, 'no_shape', 'Pick a preset/tier first.');

      const base: CreateBatonInput = { preset: shape.preset };
      base.customShape = shape.customShape;
      // Brief 118/120/121 — pass the full generic shape through provision:
      // collaborator tokens, trust flags, and the lifecycle overrides (soft/
      // hard disposal + burn-after-reading). The calculator's getShape()
      // produces a create-ready body; the create API accepts these as-is.
      // contentEncoding is owned by the content-mode logic below (the actual
      // uploaded bytes' encoding wins), not the shape.
      if (shape.tokens && shape.tokens.length > 0) base.tokens = shape.tokens;
      if (shape.flags) base.flags = shape.flags;
      if (shape.onExpiration) base.onExpiration = shape.onExpiration;
      if (shape.deleteOnAllTokensConsumed !== undefined) {
        base.deleteOnAllTokensConsumed = shape.deleteOnAllTokensConsumed;
      }
      if (name.trim()) base.name = name.trim();
      if (description.trim()) base.description = description.trim();

      // Create with requestPresignedUpload, then PUT the bytes to the URL.
      const createViaPresigned = async (
        contentType: string,
        contentEncoding: 'utf-8' | 'binary',
        blob: Blob,
      ): Promise<string> => {
        const created = await createBaton({
          ...base,
          contentType,
          contentEncoding,
          requestPresignedUpload: true,
        });
        const url = created.meta?.presignedUploadUrl;
        if (!url) {
          throw new ApiError(
            502,
            'no_presigned_url',
            'Server did not return a presigned upload URL.',
          );
        }
        await putPresigned(url, blob, contentType);
        return created.data.id;
      };

      // Correctness net: `MAX_INLINE_BYTES` is only a fast-path hint. If the
      // live `baton.upload.max_single_call_bytes` tunable is below it, an
      // inline create in the gap range is rejected 422 INLINE_CONTENT_TOO_LARGE
      // — fall that same content over to the presigned path.
      const inlineTooLarge = (e: unknown): boolean =>
        e instanceof ApiError && e.code === 'INLINE_CONTENT_TOO_LARGE';

      if (mode === 'file' && file) {
        const contentType = file.type || 'application/octet-stream';
        // Fast path: a file past the inline hint goes straight to presigned.
        if (file.size > MAX_INLINE_BYTES) {
          return createViaPresigned(contentType, 'binary', file);
        }
        try {
          const created = await createBaton({
            ...base,
            content: await fileToBase64(file),
            contentEncoding: 'base64',
            contentType,
          });
          return created.data.id;
        } catch (e) {
          if (inlineTooLarge(e)) {
            return createViaPresigned(contentType, 'binary', file);
          }
          throw e;
        }
      }

      if (text) {
        try {
          const created = await createBaton({
            ...base,
            content: text,
            contentEncoding: 'utf-8',
            contentType: textType,
          });
          return created.data.id;
        } catch (e) {
          if (inlineTooLarge(e)) {
            return createViaPresigned(
              textType,
              'utf-8',
              new Blob([text], { type: textType }),
            );
          }
          throw e;
        }
      }

      // No content → an empty baton; append later.
      const created = await createBaton({ ...base });
      return created.data.id;
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: ['batons'] });
      navigate(`/dashboard/batons/${encodeURIComponent(id)}`);
    },
  });

  const canProvision = Boolean(shape) && !provision.isPending;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/dashboard/batons">
            <ArrowLeft className="h-4 w-4" /> Batons
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Create a baton</h1>
        <p className="text-muted-foreground">
          Pick a shape, price it, add content, and provision. You'll land on
          the new baton's detail page.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>1 · Shape &amp; price</CardTitle>
            <CardDescription>
              Choose a catalog bundle or a custom shape. The price resolves
              against the live catalog.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BatonPricingCalculator onShapeChange={(d) => setShape(d.shape)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2 · Content</CardTitle>
            <CardDescription>
              Inline text/CSV, or a file. Files over{' '}
              {Math.round(MAX_INLINE_BYTES / 1_000_000)} MB upload directly to
              storage via a presigned URL.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={mode === 'text' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('text')}
              >
                Inline text
              </Button>
              <Button
                type="button"
                variant={mode === 'file' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('file')}
              >
                File
              </Button>
            </div>

            {mode === 'text' ? (
              <div className="space-y-2">
                <Label htmlFor="baton-text">Text</Label>
                <textarea
                  id="baton-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={6}
                  placeholder="Paste text or CSV… (leave empty to provision an empty baton and append later)"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                />
                <div className="flex items-center gap-2 text-sm">
                  <Label htmlFor="baton-text-type" className="text-muted-foreground">
                    Content type
                  </Label>
                  <select
                    id="baton-text-type"
                    value={textType}
                    onChange={(e) => setTextType(e.target.value)}
                    className="rounded border border-input bg-background px-2 py-1"
                  >
                    <option value="text/plain">text/plain</option>
                    <option value="text/csv">text/csv</option>
                    <option value="application/json">application/json</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="baton-file">File</Label>
                <Input
                  id="baton-file"
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <p className="text-xs text-muted-foreground">
                    {file.name} · {(file.size / 1_000_000).toFixed(2)} MB ·{' '}
                    {file.size > MAX_INLINE_BYTES
                      ? 'presigned upload'
                      : 'inline'}
                  </p>
                ) : null}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="baton-name">Name (optional)</Label>
                <Input
                  id="baton-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. q3-handoff"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="baton-desc">Description (optional)</Label>
                <Input
                  id="baton-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            {provision.error ? (
              <Alert variant="destructive">
                <AlertTitle>
                  Couldn't provision (
                  {provision.error instanceof ApiError
                    ? provision.error.code
                    : 'unknown'}
                  )
                </AlertTitle>
                <AlertDescription>
                  {provision.error instanceof ApiError
                    ? provision.error.message
                    : 'Please try again.'}
                </AlertDescription>
              </Alert>
            ) : null}

            <Button
              type="button"
              disabled={!canProvision}
              onClick={() => provision.mutate()}
            >
              {provision.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Provisioning…
                </>
              ) : (
                'Provision'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
